import BaseModule from './BaseModule';
import Factory from "../libs/Factory";
import {swiperSetup} from "../libs/SwiperSetup";
import {requireElement, delegateListener} from "../libs/Helpers";
import Swiper from "swiper";
import {SwiperOptions} from "swiper/types";

const COLOR_SELECTOR = '.vmcc-cab-item.active .vmcc-cab-color-selectors span';
const FRONT_BACK_GROUP = '.vmcc-cab-images_color-group';
const VIEW_SWITCH = '.vmcc-cab-item__switch';

const VIEWS = ['front','back','side'] as const;
type VIEW = typeof VIEWS[number];

export default class VehicleModelColoursCab extends BaseModule {
    declare el: HTMLElement;
    activeSlider: number = 0;
    colorSwiper: Swiper[] = [];

    cabSelectedElement!: HTMLElement;
    cabSwitcherElement!: HTMLElement;

    currentViewPosition: VIEW = 'front';
    availableViews: VIEW[] = ['front'];

    constructor(el: HTMLElement, factory: Factory) {
        super(el, factory, 'VehicleModelColoursCab');

        this.cabSwitcherElement = requireElement(el, '.vmcc-cab-list');
        this.cabSelectedElement = requireElement(el, '.vmcc-cab-selected');

        this.initFrontBackSwitchListener();
        this.initColorSelectListener();
        this.setActiveMainCab();
        this.activeMainCabChange();

        this.el.querySelectorAll<HTMLElement>('.vmcc-cab-selected .vmcc-cab-images')?.forEach((element) => {
            const type = this.el.dataset.type || '';
            const swiperConfig: SwiperOptions = swiperSetup(element, type, this.el.dataset.autoplay === 'true');
            if(type === 'fancy'){
                swiperConfig.slidesPerView = 1;
                swiperConfig.breakpoints = {...swiperConfig.breakpoints, 767: {slidesPerView: 2}};
            }

            swiperConfig.on = {
                slideChange: (swiper: Swiper) => {
                    const children = [...this.cabSelectedElement.querySelectorAll(COLOR_SELECTOR)];
                    this.selectChild(children, swiper.activeIndex);
                },
            };

            return this.colorSwiper.push(new Swiper(element, swiperConfig));
        });
    }

    activeMainCabChange() {
        delegateListener(this.cabSwitcherElement, 'click', '.vmcc-cab-title', (event, target) => {
            event.preventDefault();

            const children = [...target.parentNode!.children];
            let idx = children.indexOf(target);

            this.selectChild(children, idx);
            this.setActiveMainCab(idx);
        });
    }

    setActiveMainCab(idx = 0) {
        this.cabSelectedElement.querySelectorAll('.vmcc-cab-item').forEach((item, key) => {
            item.classList.toggle('active', key === idx);
        });
        this.activeSlider = idx;
        // check how many images are in the active cabs color group ie front + back
        const imgCount = this.cabSelectedElement.querySelectorAll('.vmcc-cab-item.active .vmcc-cab-images_color-group:first-child img').length;
        // hide front/back switcher if not enough
        const cabSwitcher = requireElement(this.el, VIEW_SWITCH);

        this.availableViews = ['front'];
        if (imgCount > 1) {
            this.availableViews.push('back');
        }
        if (imgCount > 2) {
            this.availableViews.push('side');
        }

        cabSwitcher.classList.toggle('hide', imgCount < 2);
    }

    initColorSelectListener() {
        delegateListener(this.cabSelectedElement, 'click', COLOR_SELECTOR,  (event, target) => {
            event.preventDefault();

            const children = [...target.parentNode!.children];
            let idx = children.indexOf(target);

            // this.selectChild(children, idx);
            this.colorSwiper[this.activeSlider].slideTo(idx);
        });
    }

    selectChild(children: Element[]|NodeListOf<Element>, idx: number, className = 'active'){
        children.forEach((child, key) => {
            child.classList.toggle(className, key === idx);
        });
    }

    nextView(): VIEW {
        return this.availableViews[(this.availableViews.indexOf(this.currentViewPosition) + 1) % this.availableViews.length];
    }

    initFrontBackSwitchListener() {
        delegateListener(this.el, 'click', VIEW_SWITCH, (event) => {
            event.preventDefault();

            const current = this.currentViewPosition;
            this.currentViewPosition = this.nextView();

            this.cabSelectedElement
                .querySelectorAll(FRONT_BACK_GROUP)
                .forEach((group) => {
                    group.classList.remove(current);
                    group.classList.add(this.currentViewPosition);
                });
        });
    }

    private requireAll<T extends HTMLElement = HTMLElement>(selector: string): NodeListOf<T> {
        const el = this.el.querySelectorAll<T>(selector);
        if (!el || el.length === 0) {
            throw new Error(`Missing required elements: ${selector}`);
        }

        return el;
    }
}
